import { toAbsoluteUrl } from "../../_theme/helpers";
import { memo } from "react";
import HeroSection from "./Components/HeroSection";
import { envConfig } from "../../utils/config";
import { useDashboardTabEntry } from "../modules/Dashboard/core/tabRights";

interface Feature {
    id: number;
    text: string;
}

// Constants
const FEATURES_DATA: Feature[] = [
    {
        id: 1,
        text: "Access Compliant, Investor-Ready Opportunities: Tap into a selection of thoroughly assessed, low-risk ventures that are poised for investment"
    },
    {
        id: 2,
        text: "Appreciate the Broader Perspective: Every initiative is thoughtfully aligned with public infrastructure objectives, producing measurable economic, social, and environmental advantages."
    },
    {
        id: 3,
        text: "Clear Process, Reliable Outcomes: Engage with well-defined governance, impact, and delivery structures designed to enhance your confidence throughout the entire process."
    },
    {
        id: 4,
        text: "Collaborate with Assurance: Effortlessly connect with both public and private stakeholders, exchange insights, and co-invest in projects that will influence the future of the Western Cape."
    }
];

const COMPANIES_DATA: Feature[] = [
    {
        id: 1,
        text: "Explore dynamic dashboards (Gigamap) by sector, region, and project scale.​"
    },
    {
        id: 2,
        text: "Download detailed proposals instantly for deeper review.​"
    },
    {
        id: 3,
        text: "Engage directly with project teams using built-in communication tools - whether you prefer chat, call-back, or requesting more info.​​"
    },
];

const IntroductionSection: React.FC = memo(() => (
    <div className="row section-padding bg-white" id="introduction-section">
        <div className="col-md-12 px-10 px-sm-15 px-md-10 px-lg-20 px-xl-20">
            <p className="penoptic-evaluation-section-title mb-2">Investor Prospectus, the gateway of opportunity</p>
            <p className="section-content">
                Here ambition meets assurance as you explore a curated portfolio of projects engineered for impact, transparency, and sustainable growth. All projects contained in the portfolio are guaranteed to be meticulously assessed and benchmarked against South Africa’s highest governance and compliance standards. In other words all pipeline projects can be trusted to be robust, policy-aligned, and investment-ready - offering a clear pathway that facilities trusted partnerships.
            </p>
        </div>
    </div>
));


// Reusable list renderer
const FeatureList: React.FC<{ items: Feature[] }> = memo(({ items }) => (
    <ul className="fs-5 fw-light">
        {items.map((item) => (
            <li key={item.id} className="mb-3 section-content">
                {item.text}
            </li>
        ))}
    </ul>
));

const OutcomeSection: React.FC = memo(() => (
    <div className="row d-flex flex-column-reverse flex-lg-row section-padding g-10 bg-white align-items-center m-0">
        <div className="col-md-6 px-10 px-sm-15 px-md-10 px-lg-20 px-xl-20  pe-lg-5 pe-xl-5">
            <div>
                <p className="section-content mb-5">
                    A dynamic collaboration space where curiosity sparks action and your engagement drives progress. The WCG platform makes it simple to compare, connect, and commit:
                </p>
                <FeatureList items={COMPANIES_DATA} />
                <p className="section-content mt-5">
                    Join a trusted bridge between private capital and public vision. Accelerate your journey from interest to investment, all within an environment designed for clarity, confidence, and collaboration.
                </p>
            </div>
        </div>
        <div className="col-md-6 px-10 px-sm-15 px-md-10 px-lg-10 px-xl-20  ps-lg-5 ps-xl-5">
            <img
                src={toAbsoluteUrl("media/images/the-outcome.png")}
                alt="Investment-Ready Portfolio Outcome"
                className="w-100 h-100 rounded"
                loading="lazy"
            />
        </div>
    </div>
));


const VisionSection: React.FC = memo(() => (
    <div className="row bg-white align-items-center">
        <div className="col-md-6 px-10 px-sm-15 px-md-10 px-lg-20 px-xl-20 pe-lg-5 pe-xl-5">
            <img
                src={toAbsoluteUrl("media/images/WhyPartnerwithUs.jpg")}
                alt="Western Cape Infrastructure Vision"
                className="w-100 h-100 rounded"
                loading="lazy"
            />
        </div>
        <div className="col-md-6 px-10 px-sm-15 px-md-10 px-lg-20 px-xl-20 ps-lg-5 ps-xl-5">
            <div>
                <p className="penoptic-evaluation-section-title mb-5">Why Partner with Us?</p>
                <FeatureList items={FEATURES_DATA} />
            </div>
        </div>
    </div>
));

// Main Component
const InvestorProspectus: React.FC = () => {
    const dashboardEntry = useDashboardTabEntry('prospectus', 'the Investor Prospectus');
    return (
        <div className="overflow-y-auto overflow-x- hidden about-us bg-white">
            <HeroSection videoSrc={`${envConfig.APP_MEDIA_URL}videos/public-pages/investor-prospectus.mp4`}
                fallbackImage={`${envConfig.APP_MEDIA_URL}images/public-pages/investor-prospectus.webp`}
                title="The Investor Prospectus: Clarity, Confidence, Collaboration"
                subtitle="Connecting you to infrastructure projects purpose-built for sustainable growth and partnership."
                imageSrc={toAbsoluteUrl('media/images/investment.png')}
                imageAlt="Investor Prospectus"
                imageClassName="w-150px"
                {...dashboardEntry}
                scrollParentId="introduction-section" />
            {/* <div className="public-pages-container"> */}
            <IntroductionSection />
            <VisionSection />
            <OutcomeSection />
            {/* </div> */}
        </div>
    )
};

export default InvestorProspectus;